import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  TextField, 
  InputAdornment, 
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Divider,
  Paper,
  Tabs,
  Tab,
  Container
} from '@mui/material';
import { 
  Search as SearchIcon, 
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileCopy as CopyIcon,
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as DocIcon,
  Article as TextIcon
} from '@mui/icons-material';
import { ListItemIcon, ListItemText } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { templateAPI } from '../services/api';
import FileUploadComponent from '../components/documents/FileUploadComponent';

// Template category options
const CATEGORIES = [
  'All Categories',
  'Employment',
  'Confidentiality',
  'Services',
  'Real Estate',
  'Intellectual Property',
  'Corporate',
  'Other'
];

const TemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<any[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // File upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await templateAPI.getAll();
        setTemplates(response.data);
        setFilteredTemplates(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching templates:', error);
        // Use mock data for demo purposes
        const mockTemplates = [
          { id: '1', name: 'Non-Disclosure Agreement', category: 'Confidentiality', description: 'Standard NDA template for protecting confidential information.', lastModified: '2023-05-15' },
          { id: '2', name: 'Employment Contract', category: 'Employment', description: 'Standard employment agreement template with customizable terms.', lastModified: '2023-05-10' },
          { id: '3', name: 'Service Agreement', category: 'Services', description: 'Template for service provider agreements with customizable terms and conditions.', lastModified: '2023-05-05' },
          { id: '4', name: 'Lease Agreement', category: 'Real Estate', description: 'Residential lease agreement template with standard terms and conditions.', lastModified: '2023-04-28' },
          { id: '5', name: 'Software License Agreement', category: 'Intellectual Property', description: 'Template for software licensing with customizable terms.', lastModified: '2023-04-20' },
        ];
        setTemplates(mockTemplates);
        setFilteredTemplates(mockTemplates);
        setLoading(false);
      }
    };
    
    fetchTemplates();
  }, []);
  
  // Filter templates based on search term and category
  useEffect(() => {
    let result = templates;
    
    // Filter by search term
    if (searchTerm) {
      result = result.filter(template => 
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        template.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by category
    if (selectedCategory !== 'All Categories') {
      result = result.filter(template => template.category === selectedCategory);
    }
    
    setFilteredTemplates(result);
  }, [searchTerm, selectedCategory, templates]);
  
  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, template: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedTemplate(template);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Template action handlers
  const handleEditTemplate = () => {
    handleMenuClose();
    navigate(`/templates/edit/${selectedTemplate.id}`);
  };
  
  const handleUseTemplate = () => {
    handleMenuClose();
    navigate(`/documents/new?templateId=${selectedTemplate.id}`);
  };
  
  const handleDuplicateTemplate = async () => {
    handleMenuClose();
    try {
      // In a real app, you would call the API to duplicate the template
      // For demo purposes, we'll just show a success message
      alert(`Template "${selectedTemplate.name}" duplicated successfully!`);
    } catch (error) {
      console.error('Error duplicating template:', error);
    }
  };
  
  const handleDeleteConfirmOpen = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteTemplate = async () => {
    setDeleteDialogOpen(false);
    try {
      await templateAPI.delete(selectedTemplate.id);
      // Remove the deleted template from the list
      setTemplates(templates.filter(t => t.id !== selectedTemplate.id));
    } catch (error) {
      console.error('Error deleting template:', error);
      // For demo purposes, we'll still remove it from the UI
      setTemplates(templates.filter(t => t.id !== selectedTemplate.id));
    }
  };
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  
  // Handle file upload
  const handleFileUpload = (file: File) => {
    setUploadedFile(file);
    // Navigate to the document upload page with the file
    navigate('/documents/upload');
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">Templates & Documents</Typography>
        <Box>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate('/templates/new')}
            sx={{ mr: 2 }}
          >
            Create Template
          </Button>
          <Button 
            variant="outlined" 
            color="primary"
            onClick={() => navigate('/documents/upload')}
            startIcon={<UploadIcon />}
          >
            Upload Document
          </Button>
        </Box>
      </Box>
      
      {/* Tabs for Templates and Upload */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Browse Templates" />
          <Tab label="Upload Document" />
        </Tabs>
      </Box>
      
      {/* Templates Tab Content */}
      <Box hidden={activeTab !== 0}>
        {/* Search and Filter */}
        <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search templates..."
            variant="outlined"
            size="small"
            sx={{ flexGrow: 1 }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as string)}
              label="Category"
            >
              {CATEGORIES.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        {/* Templates Grid */}
        {filteredTemplates.length > 0 ? (
          <Grid container spacing={3}>
            {filteredTemplates.map((template) => (
              <Grid item xs={12} sm={6} md={4} key={template.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h6" component="h2" gutterBottom>
                        {template.name}
                      </Typography>
                      
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, template)}>
                        <MoreIcon />
                      </IconButton>
                    </Box>
                    
                    <Chip 
                      label={template.category} 
                      size="small" 
                      sx={{ mb: 2 }} 
                    />
                    
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {template.description}
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary">
                      Last modified: {new Date(template.lastModified).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                  
                  <CardActions>
                    <Button size="small" onClick={() => handleUseTemplate()}>Use Template</Button>
                    <Button size="small" onClick={() => handleEditTemplate()}>Edit</Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No templates found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filter criteria
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Upload Document Tab Content */}
      <Box hidden={activeTab !== 1}>
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" gutterBottom>Upload Document</Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Upload your contract document to edit, analyze, and enhance it with AI.
          </Typography>
          
          <FileUploadComponent 
            onFileUpload={handleFileUpload}
            allowedFileTypes={['.pdf', '.doc', '.docx', '.txt']}
            maxFileSizeMB={10}
          />
        </Paper>
        
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>How It Works</Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>1. Upload</Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload your contract document in PDF, Word, or text format.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <EditIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>2. Edit & Enhance</Typography>
                <Typography variant="body2" color="text.secondary">
                  Edit your document and use AI to improve clauses, analyze risks, and suggest alternatives.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <PdfIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>3. Download & Share</Typography>
                <Typography variant="body2" color="text.secondary">
                  Download your enhanced document or share it securely with stakeholders.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      
      {/* Template Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditTemplate}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleUseTemplate}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Use Template</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDuplicateTemplate}>
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteConfirmOpen} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedTemplate?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteTemplate} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TemplatesPage; 